import type { Tracker } from '@archipro-website/tracker';
import type { CreateEnquirySource } from '~/modules/enquiry';
import { readEmailAttribution } from './emailAttributionCookie';

interface CreatedEnquiryProps {
    source: CreateEnquirySource;
    isMemberGenerated?: boolean;
    /**
     * How far a bulk submission fans out. Absent for a single enquiry.
     *
     * This event fires once per submission whatever it fans out to, so without
     * these counts a bulk enquiry to fifty professionals — which creates fifty
     * `Enquiry` records — is indistinguishable from one enquiry to one.
     */
    fanOut?: { itemCount: number; supplierCount: number };
    /**
     * The professional being enquired with. Required for the enquiry to count
     * towards that client's advertising conversions rather than only site-wide
     * ones.
     */
    professionalId?: number;
}

export const trackCreatedEnquiry = (
    tracker: Tracker,
    props: CreatedEnquiryProps,
) => {
    const { source, isMemberGenerated, fanOut, professionalId } = props;
    // ExtraData is one of only two custom keys the warehouse pipeline keeps, so
    // everything beyond the source rides inside it as JSON.
    //
    // Attribution goes in here rather than replacing Source: the enquiry-funnel
    // charts filter on the surface (e.g. `!= 'InPage'`), so Source must keep
    // saying which control was used while `attribution` says what campaign drove
    // it. QDDSourceTrend prefers `attribution` when present.
    const attribution = readEmailAttribution();
    const extraData = {
        ...(isMemberGenerated && { memberGenerated: true }),
        ...(fanOut && {
            bulk: true,
            itemCount: fanOut.itemCount,
            supplierCount: fanOut.supplierCount,
        }),
        ...(attribution && {
            attribution: attribution.source,
            ...(attribution.submissionId && {
                attributionSubmissionId: attribution.submissionId,
            }),
        }),
    };
    void tracker.log('CreatedEnquiry', {
        url: new URL(window.location.href),
        professionalId,
        data: {
            Source: source,
            ProfessionalID: professionalId,
            ExtraData: Object.keys(extraData).length
                ? JSON.stringify(extraData)
                : undefined,
        },
    });
};
